import { Alert, Card, Space, Typography } from 'antd'
import { useGeckoLocale } from 'features/locale'

import {
  getInspectionScopeTotals,
  getTargetDeviation,
} from './inspectionScopeTotals'
import { InspectionArea, PlanningAreasConfig } from './planningTypes'
import { formatScope, scopeBaseUnit } from './scopeMeasurement'
import { ScopeType, TechniqueCatalogue } from './techniqueTypes'

export const ZoneQuantitySummary = ({
  areas,
  requiredArea,
  config,
  catalogue,
}: {
  areas: readonly InspectionArea[]
  requiredArea: number | undefined
  config: PlanningAreasConfig
  catalogue: TechniqueCatalogue
}) => {
  const { measurementSystem } = useGeckoLocale()
  if (!config.quantities) return null
  const scopeText = (num: number | undefined, scopeType: ScopeType) =>
    formatScope(
      num === undefined
        ? undefined
        : { num, unit: scopeBaseUnit(scopeType).id },
      scopeType,
      measurementSystem,
      catalogue.units.count
    )
  const totals = getInspectionScopeTotals(areas, catalogue)
  return (
    <Card title='Zone scope totals'>
      <Typography.Paragraph>
        Source required area:{' '}
        {requiredArea === undefined
          ? 'Not captured'
          : scopeText(requiredArea, 'area')}
      </Typography.Paragraph>
      <Typography.Paragraph type='secondary'>
        Totals are grouped by degradation type and technique. Overlaps and
        repeated views are not deducted; these are planned quantities, not
        unique coverage or achieved inspection results.
      </Typography.Paragraph>
      <Space orientation='vertical'>
        {totals.map((total) => {
          const degradation = catalogue.degradationTypes.find(
            (item) => item.id === total.degradationTypeId
          )
          const deviation = getTargetDeviation(total, requiredArea)
          const warn =
            deviation === 'above'
              ? config.quantities?.warnAboveTarget
              : deviation === 'below' && config.quantities?.warnBelowTarget
          return (
            <div key={total.key}>
              <Typography.Text strong>
                {degradation?.label ?? 'Unclassified'} · {total.method}
              </Typography.Text>
              <Typography.Paragraph>
                {total.locations} location(s) · Estimate:{' '}
                {total.estimated === undefined
                  ? 'Incomplete'
                  : scopeText(total.estimated, total.scopeType)}
              </Typography.Paragraph>
              {warn && (
                <Alert
                  type='warning'
                  title={`Estimated scope is ${deviation} the source required area`}
                  description='Review the allocation across techniques and any overlapping locations. The source requirement remains unchanged.'
                />
              )}
            </div>
          )
        })}
        {!totals.length && (
          <Typography.Text type='secondary'>
            No physical quantities entered yet.
          </Typography.Text>
        )}
      </Space>
    </Card>
  )
}
